import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { Modal } from '@calendly/ui/components/modal';
import { Header as PopupHeader, Body as PopupBody, Footer, Popup } from '@calendly/ui/components/popup';
import { Sheet } from '@calendly/ui/components/sheet';
import { HeadingText, BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { extendTrial } from 'features/billing/actions/extend_trial';
import { MODAL_NAMES, MODAL_ACTION, BILLING_ENTRYPOINTS } from 'features/billing/constants';
import { trialExtensionLoadingSelector, isSuccessStateSelector } from 'features/billing/trial_extension';
import { useTrackTrialExtended } from 'hooks/analytics_events';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrack, useTrackOnce } from 'hooks/analytics_events/useTrack';
import { VARIANT } from '../preface_modal/variables';
import { CalendlyLogo, ModalImage as RetrialOfferImage, FeatureGrid } from '../trial_lifecycle_modal';
const wizardIsPhoneCss = "wltjinq";
const wizardCss = "wkd1d91";
const Wizard = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Sheet ref={ref} {...props} className={classNames(wizardCss, {
    [wizardIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerCss = "h1ik9wz0";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const calendlyLogoImgCss = "c4f1kmx";
const CalendlyLogoImg = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  return <Component ref={ref} {...restProps} className={classNames(calendlyLogoImgCss, props.className)} />;
});
const closeButtonCss = "cpvfq1r";
const CloseButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(closeButtonCss, props.className)} />);
const mobileCloseButtonIsPhoneCss = "mnnd71g";
const mobileCloseButtonCss = "m7pdvtk";
const MobileCloseButton = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <IconButton ref={ref} {...props} className={classNames(mobileCloseButtonCss, {
    [mobileCloseButtonIsPhoneCss]: isPhone
  }, props.className)} />;
});
const bodyIsPhoneCss = "bpbdub7";
const bodyCss = "bysrhml";
const Body = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bodyCss, {
    [bodyIsPhoneCss]: isPhone
  }, props.className)} />;
});
const featureContainerIsPhoneCss = "f1nhji7v";
const featureContainerCss = "fpoudfr";
const FeatureContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(featureContainerCss, {
    [featureContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const imageContainerIsPhoneCss = "i12ufgbk";
const imageContainerCss = "i1niqs9r";
const ImageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(imageContainerCss, {
    [imageContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const trialStatusImgIsPhoneCss = "t167ejpt";
const trialStatusImgCss = "t4gu0vp";
const TrialStatusImg = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(trialStatusImgCss, {
    [trialStatusImgIsPhoneCss]: isPhone
  }, props.className)} />;
});
const titleContainerCss = "t12zzmin";
const TitleContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(titleContainerCss, props.className)} />;
});
const footerContainerCss = "fh41may";
const FooterContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(footerContainerCss, props.className)} />;
});
const buttonContainerIsPhoneCss = "b1ajni7r";
const buttonContainerCss = "b1o4r6gp";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, {
    [buttonContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const ModalHeader = ({
  onClose
}) => {
  const isPhone = useDeviceType('isPhone');
  if (isPhone) return null;
  return <Header>
      <CalendlyLogoImg src={CalendlyLogo} alt={I18n.t('publisher.trial_loss.calendly_logo')} />
      <HeadingText level={1}>
        {I18n.t('publisher.trial_start.heading', {
        plan: 'Teams'
      })}
      </HeadingText>
      <CloseButton onClick={onClose}>
        <CloseIcon size="large" />
      </CloseButton>
    </Header>;
};
const FeaturesList = ({
  onClose,
  onContinue,
  onContinueLoading
}) => {
  const isPhone = useDeviceType('isPhone');
  return <FeatureContainer>
      {isPhone && <MobileCloseButton onClick={onClose}>
          <CloseIcon size="large" />
        </MobileCloseButton>}
      <TitleContainer>
        <HeadingText level={2}>
          {I18n.t('publisher.retrial_offer.title')}
        </HeadingText>
        <BodyText>{I18n.t('publisher.retrial_offer.subtitle')}</BodyText>
      </TitleContainer>
      <FeatureGrid experimentValue={VARIANT} />
      <FooterContainer>
        <BodyText>
          {I18n.t('publisher.retrial_offer.footer')}
        </BodyText>
      </FooterContainer>
      <ButtonContainer>
        <Button onClick={onContinue} loading={onContinueLoading} disabled={onContinueLoading}>
          {I18n.t('publisher.retrial_offer.start_trial')}
        </Button>
      </ButtonContainer>
    </FeatureContainer>;
};
export const RetrialOfferModal = ({
  onClose
}) => {
  const dispatch = useDispatch();
  const isPhone = useDeviceType('isPhone');
  const track = useTrack();
  const trackTrialExtended = useTrackTrialExtended();
  const {
    pathname
  } = useLocation();
  const [isOpening, setIsOpening] = useState(false);
  const extensionIsLoading = useSelector(trialExtensionLoadingSelector);
  const isSuccess = useSelector(isSuccessStateSelector);
  useTrackOnce(isOpening, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      action: MODAL_ACTION.OPENING,
      modal_name: MODAL_NAMES.RETRIAL_OFFER,
      location: BILLING_ENTRYPOINTS.PAYWALL,
      path: pathname
    }
  });
  useEffect(() => {
    setIsOpening(true);
  }, []);
  const handleClose = () => {
    onClose();
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: MODAL_ACTION.DISMISSED,
        modal_name: MODAL_NAMES.RETRIAL_OFFER,
        location: BILLING_ENTRYPOINTS.PAYWALL,
        path: pathname
      }
    });
  };
  const onCloseSuccess = () => {
    window.location.reload();
  };
  const onContinue = async () => {
    // GUID for 14 day extension
    const trialId = 'e808ca44-e526-42a4-bd50-a1457af0c172';
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: MODAL_ACTION.START_TRIAL,
        modal_name: MODAL_NAMES.RETRIAL_OFFER,
        location: BILLING_ENTRYPOINTS.PAYWALL,
        path: pathname
      }
    });
    try {
      const response = await dispatch(extendTrial({
        extraDays: trialId
      })).unwrap();
      const today = new Date();
      const newTrialDateEnd = new Date(today);
      newTrialDateEnd.setDate(today.getDate() + response.extended_days);
      trackTrialExtended({
        days_to_add: response.extended_days,
        new_trial_date_end: newTrialDateEnd,
        trial_id: trialId
      });
    } catch {
      // Error state is handled by the selector
    }
  };
  if (isSuccess) {
    return <Popup onClose={onCloseSuccess} withCloseButton>
        <PopupHeader title={I18n.t('publisher.billing.hvf_trial_creation_modal.success.title')} />
        <PopupBody>
          <BodyText>
            {I18n.t('publisher.billing.hvf_trial_creation_modal.success.body')}
          </BodyText>
        </PopupBody>
        <Footer>
          <Button onClick={onCloseSuccess}>
            {I18n.t('publisher.billing.hvf_trial_creation_modal.success.continue')}
          </Button>
        </Footer>
      </Popup>;
  }
  return <Modal onClose={handleClose}>
      <Wizard>
        <ModalHeader onClose={handleClose} />
        <Body>
          {!isPhone && <FeaturesList onClose={handleClose} onContinue={onContinue} onContinueLoading={extensionIsLoading} />}
          <ImageContainer>
            <TrialStatusImg src={RetrialOfferImage} />
          </ImageContainer>
          {isPhone && <FeaturesList onClose={handleClose} onContinue={onContinue} onContinueLoading={extensionIsLoading} />}
        </Body>
      </Wizard>
    </Modal>;
};

require("./retrial_offer_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./retrial_offer_modal.js");